import { Box, Checkbox, Modal, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { SUPPORT_STEPS, getSupportMethodOptions } from "../../constants/activityLibrary";
import { closeBtnSx, ghostBtnSx, modalFooterSx, modalHeaderSx, primaryBtnSx } from "./styles";

const emptyPicks = () => SUPPORT_STEPS.map(() => []);
const hasAny = (picks) => !!picks && picks.some((arr) => arr.length);

// 4-step "what support was used" wizard shown after picking a Care Plan
// Activities item — each step's pills come from the matching Support
// Methods section (Prompting / Physical Support / Behavioral Support /
// Safety Support). Closing the wizard abandons the pick entirely, matching
// the mockup (the activity is only added to the note on the final step).
//
// Two extra modes from the mockup:
// - forDefault: invoked from the admin page's "Set default prompting" button —
//   no real activity, just saving a default picks[] for reuse.
// - a default exists + !forDefault: opens on a "Use your default?" preview
//   screen before the stepped flow (skip straight to committing the default,
//   or customize starting from a clone of it).
const SupportMethodWizard = ({ opened, activityName, activityKey, defaultSupports, forDefault, onClose, onFinish, onFinishDefault, onSaveAsDefault }) => {
  const startsOnPreview = !forDefault && hasAny(defaultSupports);
  const [mode, setMode] = useState(startsOnPreview ? "preview" : "steps");
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState(emptyPicks());
  const [setAsDefault, setSetAsDefault] = useState(false);

  useEffect(() => {
    if (opened) {
      setMode(startsOnPreview ? "preview" : "steps");
      setStep(0);
      // Editing an existing default should show what's already saved as
      // selected, not start blank — only plain "steps" mode (not the preview
      // screen) needs picks preloaded, since preview shows its own read-only pill list.
      setPicks(forDefault && hasAny(defaultSupports) ? defaultSupports.map((arr) => arr.slice()) : emptyPicks());
      setSetAsDefault(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, activityKey]);

  if (!opened) return null;

  if (mode === "preview") {
    const flat = (defaultSupports || []).flat();
    return (
      <Modal opened={opened} onClose={onClose} size={440} radius={12} zIndex={1500} overlayProps={{ blur: 3 }} padding={0} withCloseButton={false} styles={{ body: { padding: 0 } }}>
        <Box sx={modalHeaderSx}>
          <Text fz={16} fw={600} ff="Inter">{activityName}</Text>
          <Box component="button" onClick={onClose} sx={closeBtnSx}>✕</Box>
        </Box>
        <Box sx={{ padding: "18px 22px" }}>
          <Text fz={16} fw={600} ff="Inter" mb={6}>Use your default support methods?</Text>
          <Text fz={13} c="dimmed" ff="Inter" mb={14}>Apply your saved default and skip the questions, or customize for this activity.</Text>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {flat.length
              ? flat.map((name) => (
                  <Box key={name} component="span" sx={{ border: "1px solid var(--app-ink-blue)", borderRadius: 999, padding: "6px 12px", fontSize: 13, fontFamily: "Inter", background: "var(--app-tint-blue)", color: "var(--app-ink-blue)", fontWeight: 600 }}>
                    {name}
                  </Box>
                ))
              : <Text fz={13} c="dimmed" ff="Inter">No support methods in the default.</Text>}
          </Box>
        </Box>
        <Box sx={modalFooterSx}>
          <Box component="button" onClick={() => { setPicks((defaultSupports || emptyPicks()).map((a) => a.slice())); setMode("steps"); }} sx={ghostBtnSx}>
            Select support method
          </Box>
          <Box component="button" onClick={() => onFinish(flat)} sx={primaryBtnSx(false)}>
            Use default
          </Box>
        </Box>
      </Modal>
    );
  }

  const current = SUPPORT_STEPS[step];
  const options = getSupportMethodOptions(current.section);
  const isLast = step === SUPPORT_STEPS.length - 1;

  const togglePick = (name) => {
    setPicks((prev) => {
      const next = prev.map((arr) => arr.slice());
      const idx = next[step].indexOf(name);
      if (idx >= 0) next[step].splice(idx, 1);
      else next[step].push(name);
      return next;
    });
  };

  const goNext = () => {
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    if (forDefault) {
      onFinishDefault(picks);
      return;
    }
    if (setAsDefault) onSaveAsDefault(picks);
    onFinish(picks.flat());
  };

  const goSkip = () => {
    setPicks((prev) => {
      const next = prev.map((arr) => arr.slice());
      next[step] = [];
      return next;
    });
    goNext();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={440}
      radius={12}
      zIndex={1500}
      overlayProps={{ blur: 3 }}
      padding={0}
      withCloseButton={false}
      styles={{ body: { padding: 0 } }}
    >
      <Box sx={modalHeaderSx}>
        <Text fz={16} fw={600} ff="Inter">{forDefault ? "Set Default Prompting" : activityName}</Text>
        <Box component="button" onClick={onClose} sx={closeBtnSx}>✕</Box>
      </Box>

      <Box sx={{ padding: "18px 22px" }}>
        <Box
          component="span"
          sx={{
            display: "inline-block", fontSize: 12, fontWeight: 600, color: "var(--app-ink-amber)",
            background: "var(--app-tint-amber)", padding: "3px 10px", borderRadius: 999, marginBottom: 10,
            fontFamily: "Inter",
          }}
        >
          Step {step + 1} of {SUPPORT_STEPS.length}
        </Box>
        <Text fz={16} fw={600} ff="Inter" mb={14}>{current.question}</Text>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {options.map((name) => {
            const on = picks[step].includes(name);
            return (
              <Box
                key={name}
                component="button"
                onClick={() => togglePick(name)}
                sx={{
                  border: on ? "1px solid var(--app-ink-blue)" : "1px solid var(--app-border)",
                  borderRadius: 999, padding: "6px 12px", fontSize: 13, fontFamily: "Inter",
                  background: on ? "var(--app-tint-blue)" : "var(--app-surface)", color: on ? "var(--app-ink-blue)" : "var(--app-text)",
                  fontWeight: on ? 600 : 400, cursor: "pointer", userSelect: "none",
                  "&:hover": { filter: "brightness(.97)" },
                }}
              >
                {name}
              </Box>
            );
          })}
          {!options.length && (
            <Text fz={13} c="dimmed" ff="Inter">No support methods configured for this section.</Text>
          )}
        </Box>
        {!forDefault && (
          <Checkbox
            mt={16}
            size="sm"
            label="Set these selections as the default"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.currentTarget.checked)}
            styles={{ label: { fontFamily: "Inter", fontSize: 13 } }}
          />
        )}
      </Box>

      <Box sx={modalFooterSx}>
        {step > 0 && (
          <Box component="button" onClick={() => setStep((s) => s - 1)} sx={ghostBtnSx}>
            Back
          </Box>
        )}
        {!isLast && (
          <Box component="button" onClick={goSkip} sx={ghostBtnSx}>
            Skip
          </Box>
        )}
        <Box component="button" onClick={goNext} sx={primaryBtnSx(false)}>
          {isLast ? (forDefault ? "Save default" : "Add to note") : "Next"}
        </Box>
      </Box>
    </Modal>
  );
};

export default SupportMethodWizard;
